import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Loader2, Upload, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import ScrollFadeWrapper from "@/components/ui/scroll-fade-wrapper";
import { Textarea } from "@/components/ui/textarea";
import Typography from "@/components/ui/typography";
import config from "@/config";
import {
  AI_CATEGORY_LABELS,
  AICategory,
  CreateMarketplaceProfilePayload,
  createOrUpdateMarketplaceProfile,
  getMyMarketplaceProfile,
  MarketplaceProfileKeys,
} from "@/features/marketplace/api/marketplace-profiles";
import { useToast } from "@/hooks/use-toast";

import { TestimonialsSection } from "./marketplace-testimonials";

// Flattened list of all predefined tools
const ALL_TOOLS = [
  "Zapier",
  "N8N",
  "Make.com",
  "Lindy",
  "Gumloop",
  "OpenAI API",
  "Claude API",
  "Anthropic",
  "GPT-4",
  "Midjourney",
  "Stable Diffusion",
  "Webflow",
  "Bubble",
  "Airtable",
  "Notion",
  "Coda",
  "React",
  "TypeScript",
  "Python",
  "Node.js",
  "Next.js",
  "Google Analytics",
  "Mixpanel",
  "Tableau",
  "Figma",
  "Adobe XD",
  "Canva",
  "Photoshop",
];

const marketplaceSchema = z.object({
  profilePhoto: z.string().min(1, "Profile photo is required"),
  title: z
    .string()
    .min(1, "Title is required")
    .max(100, "Title must be less than 100 characters"),
  location: z
    .string()
    .min(1, "Location is required")
    .max(100, "Location must be less than 100 characters"),
  background: z
    .string()
    .min(1, "Background is required")
    .max(500, "Background must be less than 500 characters"),
  oneLiner: z
    .string()
    .min(1, "One liner is required")
    .max(200, "One liner must be less than 200 characters"),
  skills: z.array(z.string()).min(1, "At least one skill is required"),
  aiCategory: z
    .array(z.nativeEnum(AICategory))
    .min(1, "At least one AI category is required"),
});

type MarketplaceFormValues = z.infer<typeof marketplaceSchema>;

const MarketplaceSection = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [skillInput, setSkillInput] = React.useState("");
  const [photoError, setPhotoError] = React.useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const hasSetInitialValues = React.useRef(false);

  const form = useForm<MarketplaceFormValues>({
    resolver: zodResolver(marketplaceSchema),
    mode: "onBlur",
    defaultValues: {
      profilePhoto: "",
      title: "",
      location: "",
      oneLiner: "",
      background: "",
      skills: [],
      aiCategory: [],
    },
  });

  // Fetch existing profile
  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: [MarketplaceProfileKeys.MY_PROFILE],
    queryFn: getMyMarketplaceProfile,
  });

  // Update form when profile is loaded
  React.useEffect(() => {
    if (profile && !hasSetInitialValues.current) {
      form.reset({
        profilePhoto: profile.profileImageUrl || "",
        title: profile.title || "",
        location: profile.location || "",
        oneLiner: profile.oneLiner || "",
        background: profile.background || "",
        skills: profile.skills || [],
        aiCategory: (profile.aiCategory as AICategory[]) || [],
      });
      hasSetInitialValues.current = true;
    }
  }, [profile, form]);

  // Save profile mutation
  const { mutate: saveProfile, isPending } = useMutation({
    mutationFn: createOrUpdateMarketplaceProfile,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [MarketplaceProfileKeys.MY_PROFILE],
      });
      toast({
        title: "Profile saved successfully!",
        description: "Your marketplace profile has been updated.",
      });
      // Reset form with server response (has CloudFront URL instead of base64)
      form.reset({
        profilePhoto: data.profileImageUrl || "",
        title: data.title || "",
        location: data.location || "",
        oneLiner: data.oneLiner || "",
        background: data.background || "",
        skills: data.skills || [],
        aiCategory: (data.aiCategory as AICategory[]) || [],
      });
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || "Please try again later.";
      toast({
        title: "Failed to save profile",
        description: message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: MarketplaceFormValues) => {
    saveProfile(data as CreateMarketplaceProfilePayload);
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setPhotoError("Please upload a JPG, PNG, or WEBP image");
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      setPhotoError("Image must be less than 2MB");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      form.setValue("profilePhoto", base64String, { shouldDirty: true });
      setPhotoError("");
    };
    reader.onerror = () => {
      setPhotoError("Failed to read image file");
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    form.setValue("profilePhoto", "", { shouldDirty: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Skills autocomplete logic
  const handleAddSkill = (skill: string) => {
    const currentSkills = form.getValues("skills") || [];
    if (skill.trim() && !currentSkills.includes(skill.trim())) {
      form.setValue("skills", [...currentSkills, skill.trim()], {
        shouldValidate: true,
        shouldDirty: true,
      });
      setSkillInput("");
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    const currentSkills = form.getValues("skills") || [];
    form.setValue(
      "skills",
      currentSkills.filter((skill) => skill !== skillToRemove),
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const handleSkillKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (skillInput.trim()) {
        handleAddSkill(skillInput);
      }
    } else if (e.key === "Backspace" && skillInput === "") {
      const currentSkills = form.getValues("skills") || [];
      if (currentSkills.length > 0) {
        handleRemoveSkill(currentSkills[currentSkills.length - 1]);
      }
    }
  };

  const currentSkills = form.watch("skills") || [];
  const filteredSuggestions = ALL_TOOLS.filter(
    (tool) =>
      tool.toLowerCase().includes(skillInput.toLowerCase()) &&
      !currentSkills.includes(tool),
  );

  const displayedSuggestions = skillInput
    ? filteredSuggestions.slice(0, 8)
    : ALL_TOOLS.filter((tool) => !currentSkills.includes(tool)).slice(0, 10);

  if (isLoadingProfile) {
    return (
      <div className="bg-[#f9f9f9] rounded-2xl p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f9f9f9] rounded-2xl p-4 sm:p-6 space-y-6 overflow-hidden">
      <div className="space-y-2">
        <Typography size="l2" className="font-semibold text-[#494949]">
          Stack Landing Page
        </Typography>
        <Typography size="b2" className="text-[#767677]">
          Customize your public profile to showcase your expertise and attract
          clients.
        </Typography>
        {profile?._id && (
          <div className="mt-4 p-3 sm:p-4 bg-primary/5 border border-primary/20 rounded-lg overflow-hidden w-full sm:w-1/2">
            <div className="flex flex-col gap-3">
              <div className="min-w-0">
                <Typography
                  size="b3"
                  className="text-[#494949] font-medium mb-1"
                >
                  Your public profile
                </Typography>
                <a
                  href={`${config.marketplaceUrl}/consultant/${profile._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline block truncate"
                >
                  {`${config.marketplaceUrl}/consultant/${profile._id}`}
                </a>
              </div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${config.marketplaceUrl}/consultant/${profile._id}`,
                  );
                  toast({
                    title: "Link copied!",
                    description: "Profile URL copied to clipboard.",
                  });
                }}
                className="flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium w-full"
              >
                <Copy className="h-4 w-4" />
                Copy link
              </button>
            </div>
          </div>
        )}
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="profilePhoto"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Profile Photo</FormLabel>
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-6">
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-24 h-24 sm:w-[120px] sm:h-[120px] rounded-full border-2 border-dashed border-[#d4d4d4] hover:border-[#494949] transition-colors bg-[#fdfdfd] overflow-hidden group relative"
                    >
                      {field.value ? (
                        <>
                          <img
                            src={field.value}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              Change Photo
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-[#767677]">
                          <Upload className="h-8 w-8 mb-2" />
                          <span className="text-sm font-medium">
                            Click to upload
                          </span>
                        </div>
                      )}
                    </button>
                    {field.value && (
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="absolute top-0 right-0 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-sm text-[#767677]">
                      Upload a professional photo
                    </p>
                    <p className="text-[10px] tracking-[-0.4px] text-[#a4a4a4] mt-1">
                      JPG, PNG, or WEBP • Max 2MB
                    </p>
                    {photoError && (
                      <p className="text-xs text-red-500 mt-2">{photoError}</p>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="E.g. AI Automation Specialist"
                      className="bg-[#fdfdfd]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="E.g. San Francisco, CA"
                      className="bg-[#fdfdfd]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="oneLiner"
            render={({ field }) => (
              <FormItem>
                <FormLabel>One Liner</FormLabel>
                <FormControl>
                  <Input
                    placeholder="A catchy sentence that describes what you do..."
                    className="bg-[#fdfdfd]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="background"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Background</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Share your story, experience, and what makes you unique..."
                    className="min-h-[150px]"
                    {...field}
                  />
                </FormControl>
                <p className="text-[10px] tracking-[-0.4px] text-[#a4a4a4]">
                  Maximum 500 characters
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="skills"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Skills & Tools</FormLabel>
                <FormControl>
                  <div className="space-y-3">
                    <div
                      className="border rounded-lg p-2 bg-[#fdfdfd] min-h-[56px] flex flex-wrap gap-2 items-center cursor-text border-[#d4d4d4] outline-none ring-0 focus:outline-none focus:ring-0 focus-within:outline-none focus-within:ring-0 focus-visible:outline-none focus-visible:ring-0"
                      onClick={() =>
                        document.getElementById("skill-input")?.focus()
                      }
                    >
                      {field.value?.map((skill) => (
                        <div
                          key={skill}
                          className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5"
                        >
                          <span className="text-sm font-medium text-[#1e1e24]">
                            {skill}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveSkill(skill);
                            }}
                            className="hover:bg-primary/20 rounded-full p-0.5 text-[#767677] hover:text-[#494949] transition-colors"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}

                      <input
                        id="skill-input"
                        type="text"
                        value={skillInput}
                        onChange={(e) => setSkillInput(e.target.value)}
                        onKeyDown={handleSkillKeyDown}
                        placeholder={
                          field.value?.length === 0
                            ? "Add skills and tools..."
                            : ""
                        }
                        className="flex-1 outline-none ring-0 focus:outline-none focus:ring-0 bg-transparent min-w-[120px] text-sm text-[#1e1e24]"
                      />
                    </div>

                    <p className="text-[10px] tracking-[-0.4px] text-[#a4a4a4]">
                      Type to search or add custom skills
                    </p>

                    {displayedSuggestions.length > 0 && (
                      <div>
                        <Typography
                          size="b3"
                          className="text-[#767677] mb-2 block"
                        >
                          {skillInput ? "Suggestions" : "Popular Tools"}
                        </Typography>
                        <ScrollFadeWrapper fadeColor="rgba(249,249,249,1)">
                          <div className="flex gap-2 pb-2">
                            {displayedSuggestions.map((tool) => (
                              <button
                                key={tool}
                                type="button"
                                onClick={() => handleAddSkill(tool)}
                                className="px-3 py-1.5 rounded-lg border border-[#d4d4d4] bg-[#fdfdfd] hover:bg-[#f5f5f5] hover:border-[#494949] whitespace-nowrap text-sm transition-colors"
                              >
                                {tool}
                              </button>
                            ))}
                          </div>
                        </ScrollFadeWrapper>
                      </div>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="aiCategory"
            render={() => (
              <FormItem>
                <Typography size="l2" className="font-semibold text-[#494949]">
                  AI Categories
                </Typography>
                <Typography size="b2" className="text-[#767677]">
                  Select the AI services you offer
                </Typography>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
                  {Object.values(AICategory).map((category) => (
                    <FormField
                      key={category}
                      control={form.control}
                      name="aiCategory"
                      render={({ field }) => {
                        const isSelected = field.value?.includes(category);
                        return (
                          <FormItem key={category}>
                            <FormControl>
                              <button
                                type="button"
                                onClick={() => {
                                  const currentValue = field.value || [];
                                  const newValue = isSelected
                                    ? currentValue.filter(
                                        (value) => value !== category,
                                      )
                                    : [...currentValue, category];
                                  field.onChange(newValue);
                                }}
                                className={`w-full h-full min-h-[52px] p-3 rounded-xl border-2 text-left transition-all ${
                                  isSelected
                                    ? "border-primary bg-primary/5"
                                    : "border-[#d4d4d4] bg-[#fdfdfd] hover:border-[#494949] hover:shadow-sm"
                                }`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs sm:text-sm font-medium text-[#1e1e24] leading-tight">
                                    {AI_CATEGORY_LABELS[category]}
                                  </span>
                                  <div
                                    className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center transition-all ${
                                      isSelected
                                        ? "bg-primary"
                                        : "border border-[#d4d4d4]"
                                    }`}
                                  >
                                    <Check
                                      className={`h-3 w-3 text-white transition-opacity ${
                                        isSelected ? "opacity-100" : "opacity-0"
                                      }`}
                                    />
                                  </div>
                                </div>
                              </button>
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <div>
            <Button
              type="submit"
              disabled={isPending || !form.formState.isDirty}
              className="text-white rounded-lg h-9 px-5"
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
            <p className="text-xs text-[#767677] mt-2">
              Once you save your profile, you will get a public link to view it.
              You can edit your profile at any time.
            </p>
          </div>
        </form>
      </Form>

      {/* Testimonials Section - Only show if profile exists */}
      {profile?._id && <TestimonialsSection profileId={profile._id} />}
    </div>
  );
};

export default MarketplaceSection;
